import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Bell, Check, Play, Leaf, Mountain, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ViewAllOfferings from "@/components/ViewAllOfferings";

const LIQUID_GOLD = "#c9a84c";
const OBSIDIAN = "#0d0d0d";

export const PRODUCTS = [
    {
        slug: "island-gold-glow",
        name: "Island Gold Glow",
        tagline: "Turmeric & Coconut Soap",
        copy: "A brightening bar with Hawaiian turmeric and cold-pressed coconut oil. Deep cleanse, natural glow.",
        notes: ["Hawaiian turmeric", "Cold-pressed coconut", "Raw cane sugar"],
        image: "https://images.unsplash.com/photo-1600857544200-b2f666a9a2ec?auto=format&fit=crop&w=1400&q=80",
    },
    {
        slug: "island-sunset-glow",
        name: "Island Sunset Glow",
        tagline: "Volcanic Mineral Soap",
        copy: "Crafted with Hawaiian volcanic minerals for a grounding, skin-softening ritual.",
        notes: ["Volcanic clay", "Hibiscus infusion", "Kukui nut oil"],
        image: "https://images.unsplash.com/photo-1584305574647-0cc949a2bb9f?auto=format&fit=crop&w=1400&q=80",
    },
    {
        slug: "island-crystal-glow",
        name: "Island Crystal Glow",
        tagline: "Volcanic Mineral Soap",
        copy: "Pure clarity. Light, mineral-rich lather from the heart of the islands.",
        notes: ["Pacific sea salt", "White mineral clay", "Coconut milk"],
        image: "https://images.unsplash.com/photo-1607006344380-b6775a0824a7?auto=format&fit=crop&w=1400&q=80",
    },
];

const CRAFT_PILLARS = [
    {
        icon: Mountain,
        title: "Volcanic Mineral",
        copy: "Sourced from Hawaiian soils and sea — minerals that have shaped these islands for millennia.",
    },
    {
        icon: Leaf,
        title: "Island Botanicals",
        copy: "Turmeric, kukui, hibiscus — each ingredient hand-picked and cold-infused in small batches.",
    },
    {
        icon: Flame,
        title: "Slow-Cured",
        copy: "Patient rest time. Every bar poured by hand, inspected one at a time.",
    },
];

function ComingSoonCard({ item, onJoin }) {
    const [email, setEmail] = useState("");
    const [joined, setJoined] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const addr = (email || "").trim();
        if (!/^\S+@\S+\.\S+$/.test(addr)) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            await api.post("/waitlist", {
                email: addr,
                product_slug: item.slug,
                source: "inventory_page",
            });
            setJoined(true);
            onJoin && onJoin(item.slug);
            toast.success(`You're on the list for ${item.name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not join waitlist");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="group relative block rounded-sm overflow-hidden rise-in border border-white/10 hover:border-[color:var(--ig-gold)]/60 transition-colors duration-500"
            style={{ backgroundColor: "#161616" }}
            data-testid={`inventory-card-${item.slug}`}
        >
            <div className="relative aspect-[4/5] overflow-hidden bg-black">
                <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06]"
                />
                <span
                    className="absolute top-4 left-4 text-[0.55rem] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-current/40"
                    style={{ color: LIQUID_GOLD }}
                    data-testid={`inventory-badge-${item.slug}`}
                >
                    Coming Soon
                </span>
            </div>
            <div className="p-6 border-t border-white/10">
                <div
                    className="text-[0.55rem] tracking-[0.3em] uppercase mb-2"
                    style={{ color: LIQUID_GOLD }}
                >
                    Handcrafted · Hawaiʻi
                </div>
                <h3
                    className="font-serif text-2xl leading-tight"
                    style={{ color: LIQUID_GOLD }}
                >
                    {item.name}
                </h3>
                <p className="text-xs mt-1 text-white/70">{item.tagline}</p>
                <p className="mt-4 text-sm text-white/80 leading-relaxed">{item.copy}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                    {item.notes.map((n) => (
                        <span
                            key={n}
                            className="text-[0.6rem] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full bg-white/5 text-white/60 border border-white/10"
                        >
                            {n}
                        </span>
                    ))}
                </div>

                {joined ? (
                    <div
                        className="mt-6 flex items-center gap-2 text-sm px-4 py-3 rounded-sm bg-white/5 border border-white/10"
                        style={{ color: LIQUID_GOLD }}
                        data-testid={`inventory-joined-${item.slug}`}
                    >
                        <Check size={14} /> You're on the list. We'll email when it drops.
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-6 space-y-2" data-testid={`inventory-waitlist-form-${item.slug}`}>
                        <label className="text-[0.6rem] tracking-[0.3em] uppercase text-white/50">
                            Notify Me On Drop
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                placeholder="you@island.co"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={busy}
                                className="flex-1 px-3 py-2.5 rounded-sm bg-black/40 border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[color:var(--ig-gold)] disabled:opacity-60"
                                data-testid={`inventory-waitlist-input-${item.slug}`}
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="shrink-0 px-4 py-2.5 rounded-sm text-forest-deep text-[0.65rem] tracking-[0.25em] uppercase font-semibold hover:brightness-110 transition-all disabled:opacity-60 inline-flex items-center gap-1.5"
                                style={{ backgroundColor: LIQUID_GOLD }}
                                data-testid={`inventory-waitlist-submit-${item.slug}`}
                            >
                                <Bell size={12} /> Notify
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default function Inventory() {
    const [joinedCount, setJoinedCount] = useState(0);
    const onJoin = () => setJoinedCount((n) => n + 1);

    return (
        <div
            className="min-h-screen relative"
            style={{ backgroundColor: OBSIDIAN }}
            data-testid="inventory-page"
        >
            {/* Hero */}
            <section className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
                <div className="max-w-3xl">
                    <span
                        className="inline-flex items-center gap-2 text-[0.6rem] tracking-[0.35em] uppercase"
                        style={{ color: LIQUID_GOLD }}
                    >
                        <Sparkles size={12} /> First Drop · Coming Soon
                    </span>
                    <h1
                        className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.96] mt-6"
                        style={{ color: LIQUID_GOLD }}
                        data-testid="inventory-heading"
                    >
                        Island Gold Glow
                        <br />
                        <span className="italic text-white">Collection</span>
                    </h1>
                    <p className="mt-7 max-w-xl text-white/80 leading-relaxed text-base">
                        Handmade in Hawaiʻi. Volcanic minerals. Island botanicals.
                        Every bar poured by hand and slow-cured for weeks before it earns the Gold mark.
                        The first drop is almost ready — join the waitlist to be first in line.
                    </p>
                </div>
            </section>

            {/* ASMR Video — the big move */}
            <section className="relative max-w-7xl mx-auto px-5 md:px-10 pb-14" data-testid="inventory-asmr-section">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <span className="text-[0.6rem] tracking-[0.35em] uppercase" style={{ color: LIQUID_GOLD }}>
                            Watch The Craft
                        </span>
                        <h2 className="font-serif text-3xl md:text-4xl text-white mt-3">
                            Soap-cutting ASMR, direct from the studio
                        </h2>
                    </div>
                    <a
                        href="https://instagram.com/islandgoldglow"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[0.65rem] tracking-[0.3em] uppercase font-semibold"
                        style={{ color: LIQUID_GOLD }}
                        data-testid="inventory-follow-cta"
                    >
                        Follow for drops →
                    </a>
                </div>

                <div
                    className="mt-6 relative aspect-video rounded-sm overflow-hidden border border-white/10 bg-black"
                    data-testid="inventory-asmr-video"
                >
                    <img
                        src="https://images.unsplash.com/photo-1584305574647-0cc949a2bb9f?auto=format&fit=crop&w=1600&q=80"
                        alt="Soap-cutting ASMR studio"
                        className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <div
                                className="w-20 h-20 rounded-full flex items-center justify-center backdrop-blur-sm border"
                                style={{ borderColor: LIQUID_GOLD, backgroundColor: "rgba(0,0,0,0.5)" }}
                            >
                                <Play size={28} fill={LIQUID_GOLD} style={{ color: LIQUID_GOLD }} />
                            </div>
                            <p
                                className="text-xs tracking-[0.35em] uppercase font-semibold"
                                style={{ color: LIQUID_GOLD }}
                            >
                                ASMR Drop · Coming Soon
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Three bars as Coming Soon with waitlist */}
            <section className="relative max-w-7xl mx-auto px-5 md:px-10 pb-16">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <h2 className="font-serif text-3xl md:text-4xl" style={{ color: LIQUID_GOLD }}>
                        The First Three Bars
                    </h2>
                    {joinedCount > 0 && (
                        <span
                            className="text-[0.65rem] tracking-[0.3em] uppercase"
                            style={{ color: LIQUID_GOLD }}
                            data-testid="inventory-joined-count"
                        >
                            {joinedCount} spot{joinedCount === 1 ? "" : "s"} saved
                        </span>
                    )}
                </div>
                <div
                    className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7"
                    data-testid="inventory-grid"
                >
                    {PRODUCTS.map((item) => (
                        <ComingSoonCard key={item.slug} item={item} onJoin={onJoin} />
                    ))}
                </div>
            </section>

            <ViewAllOfferings />

            {/* Craft pillars — related content that draws interest */}
            <section className="relative border-t border-white/10 bg-black/40">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-16">
                    <div className="max-w-2xl">
                        <span className="text-[0.6rem] tracking-[0.35em] uppercase" style={{ color: LIQUID_GOLD }}>
                            Behind The Bar
                        </span>
                        <h2 className="font-serif text-3xl md:text-4xl text-white mt-3">
                            What goes into every Island Gold Glow bar
                        </h2>
                        <p className="mt-4 text-white/70">
                            Three principles — minerals from the islands, botanicals you can taste in the lather,
                            and a cure time that refuses to be rushed.
                        </p>
                    </div>
                    <div className="mt-10 grid md:grid-cols-3 gap-6" data-testid="inventory-pillars">
                        {CRAFT_PILLARS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.title}
                                    className="p-6 rounded-sm border border-white/10 bg-black/60"
                                    data-testid={`inventory-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <Icon size={20} style={{ color: LIQUID_GOLD }} />
                                    <h3 className="mt-3 font-serif text-xl text-white">{p.title}</h3>
                                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{p.copy}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Catch-all CTA */}
            <section className="relative max-w-5xl mx-auto px-5 md:px-10 py-16 text-center">
                <h2 className="font-serif text-3xl md:text-4xl" style={{ color: LIQUID_GOLD }}>
                    Be first in line
                </h2>
                <p className="mt-4 text-white/70 max-w-lg mx-auto">
                    Pick the bar that calls to you above — we'll email the moment the first batch leaves the cure room.
                </p>
                <Link
                    to="/story"
                    className="mt-6 inline-flex items-center gap-2 text-[0.65rem] tracking-[0.3em] uppercase font-semibold"
                    style={{ color: LIQUID_GOLD }}
                    data-testid="inventory-our-story-cta"
                >
                    Read Our Story →
                </Link>
            </section>
        </div>
    );
}
